/**
 * Per-user, per-panel expansion state for the Search Console cards.
 *
 * Spec v2 §3: "Expansion state persists per user. A specialist expands what they care
 * about once and it stays." An owner never expands anything and never sees this.
 *
 * ── Why localStorage and not the URL ────────────────────────────────────────────
 * The old `?tab=` param was shareable state — which tab you were on described what the
 * link pointed AT. Expansion is the opposite: it describes how one reader likes to read,
 * and putting it in the URL would mean a link shared with a colleague also imposes the
 * sender's reading habits. The `?panel=` deep link stays in the URL because it does name
 * a destination.
 *
 * ── Why the key carries the username ────────────────────────────────────────────
 * Two people share a browser profile on the same machine more often than anyone plans
 * for, and this app already keys other per-user preferences the same way (see the
 * session-table columns). A bare key would hand the second reader the first one's layout.
 *
 * ── Why panel keys are stored, not indices ──────────────────────────────────────
 * `panels` is an ordered array whose contents change as the backend adds and retires
 * panel keys (§5, F16). Storing "index 4 is open" would silently re-point at a different
 * card the first time a panel is inserted above it; a key that no longer exists simply
 * never matches, which is the correct no-op.
 */

const STORAGE_PREFIX = 'gsc:expanded';

/** Storage key for the signed-in reader, or a shared key when there is no username. */
const storageKey = (username) => `${STORAGE_PREFIX}:${username || 'anon'}`;

/**
 * Read the set of expanded panel keys.
 *
 * Every failure mode collapses to "nothing is expanded": localStorage can be absent
 * (SSR, tests), disabled (Safari private browsing throws on read), or hold a value some
 * other version of this code wrote. Collapsed is the safe default in all three — it is
 * the state §3 says must never overwhelm.
 *
 * @param {string} [username] the signed-in reader
 * @returns {Set<string>} panel keys the reader has expanded
 */
export const readExpanded = (username) => {
  try {
    const raw = window.localStorage.getItem(storageKey(username));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((k) => typeof k === 'string')) : new Set();
  } catch {
    return new Set();
  }
};

/**
 * Persist the set of expanded panel keys.
 *
 * A write failure is swallowed on purpose. Storage being full or blocked costs the
 * reader their layout on the next visit; surfacing it would cost them an error message
 * on a page whose entire job is to be calm.
 *
 * @param {string} [username] the signed-in reader
 * @param {Set<string>} keys panel keys currently expanded
 */
export const writeExpanded = (username, keys) => {
  try {
    window.localStorage.setItem(storageKey(username), JSON.stringify([...keys]));
  } catch {
    /* storage unavailable — the session still works, it just won't be remembered */
  }
};

/**
 * Toggle one panel key within a set, returning a NEW set.
 *
 * Returning a new set rather than mutating is what lets a React state setter see the
 * change; mutating in place and handing back the same reference renders nothing.
 *
 * @param {Set<string>} keys current set
 * @param {string} panelKey the panel being toggled
 * @returns {Set<string>} the updated set
 */
export const toggleExpanded = (keys, panelKey) => {
  const next = new Set(keys);
  if (next.has(panelKey)) next.delete(panelKey);
  else next.add(panelKey);
  return next;
};
